// Fiche détail d'un conseiller — vue admin (édition + infos parrain/filleuls)
import { useState, useEffect } from "react";
import {
  C, fmtEur, Badge, Btn, Input, Textarea, Modal, FormField, Alert, Section, Spinner, Select,
} from "./shared";
import api from "./api";
import { FicheContrats } from "./ContratsAdmin";

const conseillersApi = {
  get: (id) => api.get(`/conseillers/${id}`).then(r => r.data),
  update: (id, data) => api.put(`/conseillers/${id}`, data).then(r => r.data),
};

export const ConseillerDetail = ({ conseillerId, conseillers, onClose, onSaved }) => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    conseillersApi.get(conseillerId).then(setC).catch(e => setErr(e?.response?.data?.detail || "Erreur"));
  }, [conseillerId]);

  if (err) return <Modal title="Erreur" onClose={onClose}><Alert type="error"><span>⚠</span><span>{err}</span></Alert></Modal>;
  if (!c) return <Modal title="Chargement…" onClose={onClose}><Spinner /></Modal>;

  const upd = (patch) => setC({ ...c, ...patch });

  const parrainInfo = c.parrain ? conseillers.find(x => x.id === c.parrain) : null;
  const filleuls = conseillers.filter(x => x.parrain === c.id);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = {
        nom: c.nom, email: c.email, tel: c.tel, zone: c.zone, formule: c.formule,
        parrain: c.parrain || null, statut: c.statut,
        siret: c.siret || "", rcp: c.rcp || "", agrement: c.agrement || "",
        adresse: c.adresse || "", ville: c.ville || "",
        codePostal: c.codePostal || "", notes: c.notes || "",
        dateNaissance: c.dateNaissance || "",
      };
      await conseillersApi.update(conseillerId, payload);
      onSaved(); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally { setSaving(false); }
  };

  const ouvrirIntegration = async () => {
    if (!window.confirm("Ouvrir une intégration pour ce consultant ? Son accès actuel est conservé.")) return;
    try { await api.post(`/admin/conseillers/${conseillerId}/ouvrir-integration`); alert("Intégration ouverte : retrouvez-la dans « Intégrations »."); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Modal title={`${c.nom} · ${c.id}`} onClose={onClose} wide
      footer={<><Btn onClick={ouvrirIntegration} data-testid="btn-ouvrir-integration">🧩 Ouvrir une intégration</Btn><Btn onClick={onClose}>Fermer</Btn><Btn variant="gold" disabled={saving} onClick={save}>{saving ? "..." : "Enregistrer"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}

      {/* Header stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <div style={{ background: C.gray2, borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>Ventes</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.teal }}>{c.ventes}</div>
        </div>
        <div style={{ background: C.gray2, borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>CA</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>{fmtEur(c.ca)}</div>
        </div>
        <div style={{ background: C.gray2, borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>Commission</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.golddm }}>{fmtEur(c.comm)}</div>
        </div>
        <div style={{ background: C.gray2, borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>Palier</div>
          <div style={{ marginTop: 3 }}><Badge text={c.palier} /></div>
        </div>
      </div>

      {/* Réseau : parrain / filleuls */}
      <div style={{ background: `linear-gradient(135deg, ${C.navylt} 0%, #fff 100%)`, border: `1px solid ${C.navy2}22`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>🌐 Réseau</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 4 }}>Rattaché à (parrain)</div>
            {parrainInfo ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fff", borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: parrainInfo.color + "22", color: parrainInfo.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{parrainInfo.initials}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{parrainInfo.nom}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{parrainInfo.id}</div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.mid, fontStyle: "italic", padding: "6px 10px", background: "#fff", borderRadius: 8, border: `1px solid ${C.border}` }}>Direct AUPRÉA (pas de parrain)</div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 4 }}>Filleuls directs ({filleuls.length})</div>
            {filleuls.length === 0 ? (
              <div style={{ fontSize: 12, color: C.mid, fontStyle: "italic", padding: "6px 10px", background: "#fff", borderRadius: 8, border: `1px solid ${C.border}` }}>Aucun filleul pour le moment</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 100, overflowY: "auto" }}>
                {filleuls.map(f => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", background: "#fff", borderRadius: 6, border: `1px solid ${C.border}` }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: f.color + "22", color: f.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{f.initials}</div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.navy }}>{f.nom}</span>
                    <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginLeft: "auto" }}>{f.id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section : Identité & contact */}
      <Section title="Identité & contact" icon="👤">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Nom complet" required>
            <Input value={c.nom || ""} onChange={(e) => upd({ nom: e.target.value })} />
          </FormField>
          <FormField label="Email" required>
            <Input type="email" value={c.email || ""} onChange={(e) => upd({ email: e.target.value })} />
          </FormField>
          <FormField label="Téléphone">
            <Input value={c.tel || ""} onChange={(e) => upd({ tel: e.target.value })} placeholder="+33 6 …" />
          </FormField>
          <FormField label="Date de naissance">
            <Input type="date" value={c.dateNaissance || ""} onChange={(e) => upd({ dateNaissance: e.target.value })} />
          </FormField>
          <FormField label="Adresse">
            <Input value={c.adresse || ""} onChange={(e) => upd({ adresse: e.target.value })} />
          </FormField>
          <FormField label="Code postal">
            <Input value={c.codePostal || ""} maxLength={5} onChange={(e) => upd({ codePostal: e.target.value.replace(/\D/g, "") })} />
          </FormField>
          <FormField label="Ville">
            <Input value={c.ville || ""} onChange={(e) => upd({ ville: e.target.value })} />
          </FormField>
          <FormField label="Territoire d'activité">
            <Input value={c.zone || ""} onChange={(e) => upd({ zone: e.target.value })} placeholder="Guadeloupe, Paris…" />
          </FormField>
        </div>
      </Section>

      {/* Section : Statut pro & informations légales */}
      <Section title="Statut & informations légales" icon="📑">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Statut">
            <Select value={c.statut || "Actif"} onChange={(e) => upd({ statut: e.target.value })}>
              {["Actif", "En cours", "Suspendu", "Inactif"].map(s => <option key={s}>{s}</option>)}
            </Select>
          </FormField>
          <FormField label="Formule">
            <Select value={c.formule || "Conseiller"} onChange={(e) => upd({ formule: e.target.value })}>
              {["Starter", "Conseiller", "Premium"].map(f => <option key={f} value={f}>{f.replace("Conseiller", "Consultant")}</option>)}
            </Select>
          </FormField>
          <FormField label="Numéro SIRET">
            <Input value={c.siret || ""} maxLength={14} onChange={(e) => upd({ siret: e.target.value.replace(/\D/g, "") })} placeholder="14 chiffres" />
          </FormField>
          <FormField label="Numéro d'agrément (contrats)">
            <Input value={c.agrement || ""} onChange={(e) => upd({ agrement: e.target.value })} placeholder="AUP-C-0001" data-testid="fiche-agrement" />
          </FormField>
          <FormField label="N° RCP (Responsabilité Civile Pro)">
            <Input value={c.rcp || ""} onChange={(e) => upd({ rcp: e.target.value })} placeholder="Ex: RCP-2026-…" />
          </FormField>
          <FormField label="Parrain">
            <Select value={c.parrain || ""} onChange={(e) => upd({ parrain: e.target.value })}>
              <option value="">Direct AUPRÉA (aucun)</option>
              {conseillers.filter(x => x.id !== c.id).map(x => (
                <option key={x.id} value={x.id}>{x.nom} ({x.id})</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Date d'inscription (lecture seule)">
            <Input value={c.since || ""} disabled style={{ background: C.gray2, color: C.mid }} />
          </FormField>
        </div>
      </Section>

      {/* Contrats signés */}
      <Section title="Contrats" icon="✍️">
        <FicheContrats cid={conseillerId} />
      </Section>

      {/* Notes libres */}
      <Section title="Notes internes" icon="📝" defaultOpen={false}>
        <FormField label="Notes (visibles uniquement par l'admin)">
          <Textarea value={c.notes || ""} onChange={(e) => upd({ notes: e.target.value })}
            placeholder="Historique, remarques, points de vigilance…" style={{ minHeight: 100 }} />
        </FormField>
      </Section>
    </Modal>
  );
};
